import React, { forwardRef, useRef } from 'react';
import './text-field.css';
import Loader from '../loader/Loader';
import { debounce } from '../../utils';
import { TextFieldType } from '.';

interface Props {
	onChange?: (value: string, event: UIEvent) => any;
	onInput?: (value: string, event: UIEvent) => any;
	onBlur?: (value: string, event: UIEvent) => any;
	onDebouncedInput?: (value: string, event: UIEvent) => any;
	value?: string;
	defaultValue?: string;
	disabled?: boolean;
	color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'grey';
	variant?: 'underlined' | 'filled';
	size?: 'small' | 'medium' | 'large';
	loading?: boolean;
	required?: boolean;
	// iconStart?: string;
	iconEnd?: string;
	type?: TextFieldType;
	label?: string;
	helper?: string;
	name?: string;
	form?: string;
	placeholder?: string;
	onIconClick?: (event: UIEvent) => any;
	autocomplete?: string;
	autoFocus?: boolean;
	rootProps?: {
		[key: string]: any;
	};
	inputProps?: {
		[key: string]: any;
	};
	[key: string]: any;
}

const defaultVariant = 'underlined';

const TextField = forwardRef<HTMLInputElement, Props>(
	(props: Props, ref: React.ForwardedRef<HTMLInputElement>) => {
		const rootElementRef = useRef(null);

		const handleStateChange = (event: UIEvent): void => {
			const variant = props.variant ?? defaultVariant;

			const selectorClass = `.omrs-input-${variant}`;

			const rootElement = rootElementRef.current;

			// @ts-ignore
			const newValue = event.target.value;

			// @ts-ignore
			if (newValue.trim() !== '') {
				// If the input field is not empty after blur, add the class
				rootElement.classList.add('not-empty');
			} else {
				rootElement.classList.remove('not-empty');
			}
		};

		// Based on the input type return the correct data type
		const sanitizeValue = (value: string): string | number => {
			const type = props.type ?? 'text';

			if (type === 'number') {
				return value;
			}

			return value;
		};

		// Destructure all know props and leave other props to be cascaded down to the root element
		const {
			onChange,
			value,
			defaultValue,
			disabled,
			color,
			variant = defaultVariant,
			size,
			loading,
			iconStart,
			iconEnd,
			required,
			type = 'text',
			label = '',
			helper = '',
			name = '',
			form = '',
			placeholder = '',
			autocomplete = '',
			rootProps = {},
			inputProps = {},
			onIconClick,
			onInput,
			onDebouncedInput,
			...otherProps
		} = props;

		const isLoading = (loading ?? false) && !disabled;

		const isDisabled = disabled ?? false;

		const isRequired = required ?? false;

		const { classes: rootClasses = [], ...remainingRootProps } =
			rootProps || {};
		const { classes: inputClasses = [], ...remainingInputProps } =
			inputProps || {};

		const autoFocus = props.autoFocus ?? false;

		const debouncedInputHandler = debounce((value: any, event: any) => {
			// @ts-ignore
			onDebouncedInput?.(value, event);
		}, 200);

		return (
			<div className='omrs-input-group' ref={rootElementRef}>
				<label
					className={[
						'omrs-input',
						'omrs-input-' + variant,
						...(color ? ['omrs-input-' + color] : []),
						...(value ? ['not-empty'] : []),
					].join(' ')}
				>
					<input
						ref={ref}
						required={isRequired}
						disabled={isDisabled}
						type={type}
						name={name}
						form={form}
						autoFocus={autoFocus}
						autoComplete={!!autocomplete ? autocomplete : 'off'}
						placeholder={placeholder}
						className={[...inputClasses].join(' ')}
						onInput={(event) => {
							if (onInput) {
								handleStateChange(event as any);
								onInput?.(
									sanitizeValue(
										(event.target as HTMLInputElement).value
									) as string,
									event as any
								);
							}

							if (onDebouncedInput) {
								handleStateChange(event as any);

								debouncedInputHandler(
									sanitizeValue(
										(event.target as HTMLInputElement).value
									) as string,
									event
								);
							}
						}}
						onChange={(event) => {
							handleStateChange(event as any);
							onChange?.(
								sanitizeValue(
									(event.target as HTMLInputElement).value
								) as string,
								event as any
							);
						}}
						onBlur={(event) => {
							handleStateChange(event as any);
							props.onBlur?.(
								sanitizeValue(
									(event.target as HTMLInputElement).value
								) as string,
								event as any
							);
						}}
						value={value}
						defaultValue={defaultValue}
						{...remainingInputProps}
					/>
					<span className='omrs-input-label'>{label}</span>
					<span
						className={[
							'omrs-input-helper',
							...(!helper ? ['invisible'] : []),
						].join(' ')}
					>
						{helper || 'helper'}
					</span>
					{(iconEnd || isLoading) && (
						<i
							className={[
								'omrs-input-icon',
								'fa-solid',
								'fa-' + (isLoading ? '' : iconEnd),
							].join(' ')}
							onClick={(event) => {
								if (onIconClick && !isLoading) {
									onIconClick(event as any);
								}
							}}
						>
							{isLoading && <Loader size={18} variant='text' />}
						</i>
					)}
				</label>
			</div>
		);
	}
);

export default TextField;
